import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { EVENT_DETAILS_PARTICIPANT_COUNTS } from "@/api/queries/liveEventDetailsParticipantCounts";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { FunnelStateEnum } from "@/__generated__/types";
import {
  groupStatesByFunnelGroup,
  FunnelMapName,
  FunnelStateMaps,
} from "@/util/funnelGroups";

export interface ParticipantCount {
  state: FunnelStateEnum;
  value: number;
}

export interface UseLiveEventDetailsParticipantCountsReturn {
  participantCounts: ParticipantCount[] | null;
  getGroupedCounts: <T extends FunnelMapName>(
    context: T,
  ) => Record<keyof (typeof FunnelStateMaps)[T], number> | null;
  loading: boolean;
  error: unknown;
}

export const useLiveEventDetailsParticipantCounts = (
  eventId: string,
  filterState?: FilterState | null,
): UseLiveEventDetailsParticipantCountsReturn => {
  const { data, loading, error } = useQuery(EVENT_DETAILS_PARTICIPANT_COUNTS, {
    variables: {
      eventId,
      filters: filterState?.getQueryFilter() || undefined,
    },
    skip: !eventId,
  });

  const participantCounts = useMemo(() => {
    if (
      !data?.node ||
      data.node.__typename !== "LiveEvent" ||
      !data.node.participantsByFunnelState
    ) {
      return null;
    }

    return data.node.participantsByFunnelState;
  }, [data]);

  const getGroupedCounts = useMemo(() => {
    return <T extends FunnelMapName>(
      context: T,
    ): Record<keyof (typeof FunnelStateMaps)[T], number> | null => {
      if (!participantCounts) {
        return null;
      }

      return groupStatesByFunnelGroup(participantCounts, context);
    };
  }, [participantCounts]);

  return {
    participantCounts,
    getGroupedCounts,
    loading,
    error,
  };
};
